import { useEffect, useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import PanelSkeleton from "../PanelSkeleton";
import Pagination from "../Pagination";
import { toast } from "react-toastify";
import Modal from 'react-modal';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { FaRegTrashAlt, FaWhatsapp } from "react-icons/fa";
import { ImSpinner2 } from "react-icons/im";
import { BsSearch } from "react-icons/bs";
import { FiEdit } from "react-icons/fi";
import { MdClose, MdKeyboardArrowDown, MdArrowForward } from 'react-icons/md';
import { mapToArabic } from "../../utils/mapToArabic";
import ProductImage from "../ProductImage";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/autoplay";
import styles from "../../styles/specialorderspanel.module.css";

const STATUS_CARD = {
  'new':              styles.cardNew,
  'preparing':        styles.cardPrep,
  'out for delivery': styles.cardOut,
  'delivered':        styles.cardDone,
  'cancelled':        styles.cardCancel,
};

const STATUS_BADGE = {
  'new':              styles.badgeNew,
  'preparing':        styles.badgePrep,
  'out for delivery': styles.badgeOut,
  'delivered':        styles.badgeDone,
  'cancelled':        styles.badgeCancel,
};

const REQUEST_TYPE_LABEL = {
  idea: 'لدي فكرة وأرغب بتطويرها',
  suggest: 'يرغب أن نقترح عليه أفكاراً',
  product_size: 'شاهدها على حائطك',
};

const SpecialOrdersPanel = () => {
  const [searchTerm, setSearchTerm]     = useState("");
  const [searchParams, setSearchParams] = useState(new URLSearchParams());
  const [moveSearch, setMoveSearch]     = useState(false);
  const [filter, setFilter]             = useState(null);
  const [expanded, setExpanded]         = useState({});
  const [searching, setSearching]       = useState(false);
  const [changing, setChanging]         = useState(false);
  const [cancelling, setCancelling]     = useState(false);
  const inputRef  = useRef(null);
  const panelRefs = useRef({});
  const [decorModal, setDecorModal]     = useState({});
  const [exampleModal, setExampleModal] = useState({});
  const [editingPricing, setEditingPricing] = useState({});
  const [pricingForm, setPricingForm]       = useState({});
  const [savingPricing, setSavingPricing]   = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage]               = useState(10);

  const token = useSelector(state => state.auth?.token);

  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [fetchTick, setFetchTick]   = useState(0);

  const refetch = useCallback(() => setFetchTick(t => t + 1), []);

  // Scroll-aware search bar
  useEffect(() => {
    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      setMoveSearch(window.scrollY > lastScrollY);
      lastScrollY = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!searchTerm) setSearchParams(new URLSearchParams());
  }, [searchTerm]);

  // Main data fetch
  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    setLoading(true);
    setFetchError(null);

    const url = `${process.env.REACT_APP_BACKEND_URL}/api/orders/specialDashboardFetch`
      + `?page=${currentPage}&limit=${ordersPerPage}`
      + (searchParams.toString() ? `&${searchParams.toString()}` : '')
      + (filter ? `&filter=${filter}` : '');

    axios
      .get(url, { withCredentials: true, headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (!cancelled) { setData(res.data); setSearching(false); }
      })
      .catch(err => {
        if (!cancelled) {
          if (err.response?.status === 304) { /* server cache hit — keep existing data */ }
          else setFetchError(err);
          setSearching(false);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [token, currentPage, searchParams, filter, fetchTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClear = () => {
    setSearchTerm("");
    inputRef.current.focus();
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setFilter(null);
    const terms = searchTerm.trim().split(/\s+/).filter(Boolean);
    if (!terms.length) return;
    const newParams = new URLSearchParams();
    terms.forEach(t => newParams.append('search', t));
    if (searchParams.toString() !== newParams.toString()) {
      setSearching(true);
      setSearchParams(newParams);
    }
  };

  const isLoadingInitial = loading || !data;
  const orders      = data?.orders     || [];
  const totalOrders = data?.totalCount ? Number(data.totalCount) : 0;
  const totalPages  = Math.ceil(totalOrders / ordersPerPage);

  const toggleExpand = (id) => {
    const panel      = panelRefs.current[id];
    const upperPanel = panel.querySelector(".upperPanel");

    if (expanded[id]) {
      panel.style.height = `${panel.scrollHeight}px`;
      void panel.offsetHeight;
      panel.style.height = `${upperPanel.scrollHeight + 20}px`;
      setTimeout(() => setExpanded(prev => ({ ...prev, [id]: false })), 500);
    } else {
      panel.style.height = `${upperPanel.scrollHeight + 20}px`;
      void panel.offsetHeight;
      setExpanded(prev => ({ ...prev, [id]: true }));
      setTimeout(() => { panel.style.height = `${panel.scrollHeight}px`; }, 10);
    }
  };

  const nextStatus = (current) => {
    switch (current) {
      case "new":              return "preparing";
      case "preparing":        return "out for delivery";
      case "out for delivery": return "delivered";
      default:                 return null;
    }
  };

  const handleStatus = (id, userId, next) => {
    if (!next) return;
    confirmAlert({
      message: `تغيير حالة الطلب إلى ${mapToArabic(next).text} ؟`,
      buttons: [
        {
          label: 'نعم',
          onClick: async () => {
            setChanging(true);
            try {
              await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/orders/${id}/updateSpecialStatus`,
                { status: next, userId },
                { withCredentials: true, headers: { Authorization: `Bearer ${token}` } }
              );
              toast.success(<span className="toast">تم تغيير حالة الطلب إلى {mapToArabic(next).text}</span>);
              refetch();
            } catch {
              toast.error(<span className="toast">لم نتمكن من تغيير حالة الطلب. حاول مرة اخرى</span>);
            } finally {
              setChanging(false);
            }
          },
        },
        { label: 'لا' },
      ],
    });
  };

  const handleCancel = (o) => {
    const decorCloudinaryIds  = o.decorImgs?.map(img => img.cloudinary_id) || [];
    const exampleCloudinaryId = o.exampleImg?.cloudinary_id || null;

    confirmAlert({
      message: `إلغاء طلب رقم ${o.id} ؟`,
      buttons: [
        {
          label: 'نعم',
          onClick: async () => {
            setCancelling(true);
            try {
              await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/api/orders/${o.id}/specialDashboardCancel`,
                { data: { userId: o.userId, decorCloudinaryIds, exampleCloudinaryId }, withCredentials: true, headers: { Authorization: `Bearer ${token}` } }
              );
              toast.success(<span className="toast">تم إلغاء الطلب بنجاح</span>);
              refetch();
            } catch {
              toast.error(<span className="toast">لم نتمكن من إلغاء الطلب. حاول مرة اخرى</span>);
            } finally {
              setCancelling(false);
            }
          },
        },
        { label: 'لا' },
      ],
    });
  };

  const startEditPricing = (o) => {
    setPricingForm(prev => ({
      ...prev,
      [o.id]: {
        width: o.width ?? "",
        height: o.height ?? "",
        material: o.material ?? "matte",
        price: o.price ?? "",
      },
    }));
    setEditingPricing(prev => ({ ...prev, [o.id]: true }));
  };

  const cancelEditPricing = (id) => {
    setEditingPricing(prev => ({ ...prev, [id]: false }));
  };

  const handlePricingFieldChange = (id, field, value) => {
    setPricingForm(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const handleSavePricing = (o) => {
    const form = pricingForm[o.id];

    if (!form?.width || !form?.height || !form?.price) {
      toast.error(<span className="toast">أدخل المقاس والسعر</span>);
      return;
    }

    confirmAlert({
      message: `تأكيد المقاس (${form.width} × ${form.height} سم) والسعر (${form.price} ₪) للطلب رقم ${o.id} ؟`,
      buttons: [
        {
          label: 'نعم',
          onClick: async () => {
            setSavingPricing(true);
            try {
              await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/orders/${o.id}/updateSpecialPricing`,
                { userId: o.userId, width: form.width, height: form.height, material: form.material, price: form.price },
                { withCredentials: true, headers: { Authorization: `Bearer ${token}` } }
              );
              toast.success(<span className="toast">تم تحديث السعر والمقاس بنجاح</span>);
              setEditingPricing(prev => ({ ...prev, [o.id]: false }));
              refetch();
            } catch {
              toast.error(<span className="toast">لم نتمكن من تحديث السعر. حاول مرة اخرى</span>);
            } finally {
              setSavingPricing(false);
            }
          },
        },
        { label: 'لا' },
      ],
    });
  };

  const toggleFilter = (value) => setFilter(f => f === value ? null : value);

  if (!token) {
    return (
      <div className={styles.panel}>
        <p className="warn">انتهت جلستك. سجّل دخولك مجدداً للمتابعة.</p>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className={styles.panel}>
        <p className="warn">حدث خطأ اثناء تحميل البيانات. حاول مرة اخرى</p>
      </div>
    );
  }

  return (
    <div className={styles.panel}>

      {/* ── Search ── */}
      <form onSubmit={handleSearch} className={moveSearch ? "search move" : "search"}>
        <input
          type="text"
          placeholder="بحث فى الطلبات الخاصة . . ."
          maxLength={30}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          ref={inputRef}
          required
        />
        {searchTerm && <MdClose className="clear" onClick={handleClear} />}
        <button type="submit" disabled={searching} className="buttonNoBorder">
          {searching ? <ImSpinner2 className="smallspinner" /> : <BsSearch className="searchiconsmall" />}
        </button>
      </form>

      {/* ── Filter pills ── */}
      <div className={`filters ${styles.filterBar}`}>
        {[
          { value: 'new',              label: 'جديد',         cls: styles.pillNew    },
          { value: 'preparing',        label: 'جارى التحضير', cls: styles.pillPrep   },
          { value: 'out for delivery', label: 'خرج للتوصيل',  cls: styles.pillOut    },
          { value: 'delivered',        label: 'تم التوصيل',   cls: styles.pillDone   },
          { value: 'cancelled',        label: 'تم الالغاء',   cls: styles.pillCancel },
        ].map(pill => (
          <button
            key={pill.value}
            className={`${styles.filterPill} ${pill.cls}${filter === pill.value ? ` ${styles.pillActive}` : ''}`}
            onClick={() => toggleFilter(pill.value)}
          >
            <span className={styles.pillDot} />
            {pill.label}
          </button>
        ))}
      </div>

      {/* ── Content ── */}
      {isLoadingInitial ? (
        <PanelSkeleton count={2} />
      ) : (
        <>
          {orders.length === 0 ? (
            <p className="warn">لم نجد أى طلبات تتوافق مع كلمات البحث</p>
          ) : (
            <div className="panelcont" style={{ margin: "0 auto" }}>
              {orders.map((o, i) => {
                const next    = nextStatus(o.status);
                const dateStr = new Date(o.created_at).toLocaleDateString('ar', {
                  year: 'numeric', month: 'short', day: 'numeric',
                });

                return (
                  <div
                    key={o.id}
                    className={`${styles.card} ${STATUS_CARD[o.status] || ''}`}
                    ref={el => (panelRefs.current[o.id] = el)}
                    style={{ animationDelay: `${i * 0.05}s` }}
                  >
                    {/* ── Card header ── */}
                    <div className={`upperPanel ${styles.cardHead}`}>

                      <p className={styles.orderId}>
                        <span className={styles.orderIdLabel}>طلب #</span>
                        {o.id}
                      </p>

                      <span className={`${styles.statusBadge} ${STATUS_BADGE[o.status] || ''}`}>
                        <span className={styles.dot} />
                        {mapToArabic(o.status).text}
                      </span>

                      <p className={styles.customerName}>{o.name}</p>
                      <span className={styles.orderDate}>{dateStr}</span>

                      <div className={styles.wallRow}>
                        <span>الحائط</span>
                        <span className={styles.wallValue}>{o.wallWidth}</span>
                      </div>

                      <button className={styles.expandBtn} onClick={() => toggleExpand(o.id)}>
                        {expanded[o.id] ? 'إخفاء' : 'التفاصيل'}
                        <MdKeyboardArrowDown className={`${styles.expandIcon}${expanded[o.id] ? ` ${styles.expandIconOpen}` : ''}`} />
                      </button>
                    </div>

                    {/* ── Expanded body ── */}
                    {expanded[o.id] && (
                      <div className={styles.expandBody}>

                        {/* Contact + address */}
                        <div>
                          <p className={styles.sectionLabel}>بيانات التواصل</p>
                          <div className={styles.infoBlock}>
                            <div className={styles.infoRow}>
                              <span className={styles.infoLabel}>الاسم</span>
                              <p className={styles.infoValue}>{o.name}</p>
                            </div>
                            <div className={styles.infoRow}>
                              <span className={styles.infoLabel}>الهاتف</span>
                              <p className={`${styles.infoValue} ${styles.infoValueLtr}`}>{o.mobile}</p>
                              {o.mobile && (
                                <a
                                  href={`https://wa.me/${o.mobile.replace(/\D/g, "")}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={styles.whatsappBtn}
                                  title="ارسال رسالة واتساب"
                                >
                                  <FaWhatsapp />
                                </a>
                              )}
                            </div>
                            {o.email && (
                              <div className={styles.infoRow}>
                                <span className={styles.infoLabel}>البريد</span>
                                <p className={`${styles.infoValue} ${styles.infoValueLtr}`}>{o.email}</p>
                              </div>
                            )}
                            {(o.street || o.district) && (
                              <div className={styles.infoRow}>
                                <span className={styles.infoLabel}>العنوان</span>
                                <p className={styles.infoValue}>
                                  {o.street}{o.district && `، ${o.district}`}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Shipping note */}
                        {o.note && (
                          <div className={styles.noteBlock}>
                            <span className={styles.noteLabel}>ملاحظة الشحن</span>
                            {o.note}
                          </div>
                        )}

                        {/* Wall width + request type */}
                        <div>
                          <p className={styles.sectionLabel}>تفاصيل الطلب</p>
                          <div className={styles.wallBlock}>
                            <span className={styles.wallBlockLabel}>مقاس الحائط (بالمتر)</span>
                            <span className={styles.wallBlockValue}>{o.wallWidth}</span>
                          </div>
                          <div className={styles.wallBlock}>
                            <span className={styles.wallBlockLabel}>الطريقة المطلوبة</span>
                            <span className={styles.wallBlockValue}>
                              {REQUEST_TYPE_LABEL[o.request_type] ?? o.request_type}
                            </span>
                          </div>
                        </div>

                        {/* Order note */}
                        {o.order_note && (
                          <div className={styles.noteBlock}>
                            <span className={styles.noteLabel}>ملاحظات الطلب</span>
                            {o.order_note}
                          </div>
                        )}

                        {/* Linked product (product_size requests) */}
                        {o.product && (
                          <div>
                            <p className={styles.sectionLabel}>اللوحة المطلوبة</p>
                            <div className={styles.imageGrid}>
                              <div className={styles.imageThumb}>
                                <ProductImage url={o.product.image_url} alt={o.product.name} loading="lazy" />
                              </div>
                            </div>
                            <div className={styles.wallBlock}>
                              <span className={styles.wallBlockLabel}>اسم اللوحة</span>
                              <span className={styles.wallBlockValue}>{o.product.name}</span>
                            </div>
                          </div>
                        )}

                        {/* Agreed size, material & price — set by the admin after negotiating with the client */}
                        <div>
                          <p className={styles.sectionLabel}>المقاس والسعر النهائى</p>

                          {editingPricing[o.id] ? (
                            <div className={styles.pricingForm}>
                              <div className={styles.pricingField}>
                                <label>العرض (سم)</label>
                                <input
                                  type="number"
                                  min="1"
                                  value={pricingForm[o.id]?.width ?? ""}
                                  onChange={(e) => handlePricingFieldChange(o.id, "width", e.target.value)}
                                />
                              </div>
                              <div className={styles.pricingField}>
                                <label>الارتفاع (سم)</label>
                                <input
                                  type="number"
                                  min="1"
                                  value={pricingForm[o.id]?.height ?? ""}
                                  onChange={(e) => handlePricingFieldChange(o.id, "height", e.target.value)}
                                />
                              </div>
                              <div className={styles.pricingField}>
                                <label>الخامة</label>
                                <select
                                  value={pricingForm[o.id]?.material ?? "matte"}
                                  onChange={(e) => handlePricingFieldChange(o.id, "material", e.target.value)}
                                >
                                  <option value="matte">Matte</option>
                                  <option value="gloss">Luster VIP</option>
                                </select>
                              </div>
                              <div className={styles.pricingField}>
                                <label>السعر (₪)</label>
                                <input
                                  type="number"
                                  min="1"
                                  value={pricingForm[o.id]?.price ?? ""}
                                  onChange={(e) => handlePricingFieldChange(o.id, "price", e.target.value)}
                                />
                              </div>

                              <div className={styles.pricingActions}>
                                <button
                                  type="button"
                                  className={styles.pricingCancelBtn}
                                  disabled={savingPricing}
                                  onClick={() => cancelEditPricing(o.id)}
                                >
                                  إلغاء
                                </button>
                                <button
                                  type="button"
                                  className={styles.pricingSaveBtn}
                                  disabled={savingPricing}
                                  onClick={() => handleSavePricing(o)}
                                >
                                  {savingPricing ? <ImSpinner2 className="smallspinner" /> : "حفظ"}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {(o.width && o.height) || o.price != null ? (
                                <>
                                  {o.width && o.height && (
                                    <div className={styles.wallBlock}>
                                      <span className={styles.wallBlockLabel}>المقاس</span>
                                      <span className={styles.wallBlockValue}>{o.width} × {o.height} سم</span>
                                    </div>
                                  )}
                                  {o.material && (
                                    <div className={styles.wallBlock}>
                                      <span className={styles.wallBlockLabel}>الخامة</span>
                                      <span className={styles.wallBlockValue}>{o.material === 'gloss' ? 'Luster VIP' : 'Matte'}</span>
                                    </div>
                                  )}
                                  {o.price != null && (
                                    <div className={styles.wallBlock}>
                                      <span className={styles.wallBlockLabel}>السعر</span>
                                      <span className={styles.wallBlockValue}>{o.price} ₪</span>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <p className={styles.noPricing}>لم يتم الاتفاق على السعر بعد</p>
                              )}

                              <button type="button" className={styles.editPricingBtn} onClick={() => startEditPricing(o)}>
                                <FiEdit /> {o.price != null ? "تعديل المقاس والسعر" : "تحديد المقاس والسعر"}
                              </button>
                            </>
                          )}
                        </div>

                        {/* Decor images */}
                        {o.decorImgs?.length > 0 && (
                          <div>
                            <p className={styles.sectionLabel}>صور الديكور</p>
                            <div className={styles.imageGrid}>
                              {o.decorImgs.map((img, idx) => (
                                <div
                                  key={img.image_url}
                                  className={styles.imageThumb}
                                  onClick={() => setDecorModal(prev => ({ ...prev, [o.id]: true }))}
                                >
                                  <ProductImage url={img.image_url} alt={`ديكور ${idx + 1}`} loading="lazy" />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Example image */}
                        {o.exampleImg && (
                          <div>
                            <p className={styles.sectionLabel}>صورة تقريبية للفكرة</p>
                            <div className={styles.imageGrid}>
                              <div
                                className={styles.imageThumb}
                                onClick={() => setExampleModal(prev => ({ ...prev, [o.id]: true }))}
                              >
                                <ProductImage url={o.exampleImg.image_url} alt="مثال" loading="lazy" />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        {o.status !== "delivered" && o.status !== "cancelled" && (
                          <div className={styles.actions}>
                            <button
                              className={styles.nextBtn}
                              disabled={changing || cancelling}
                              onClick={() => handleStatus(o.id, o.userId, next)}
                            >
                              {changing
                                ? <ImSpinner2 className="smallspinner" />
                                : <><MdArrowForward /> {mapToArabic(next).text}</>
                              }
                            </button>

                            <button
                              className={`${styles.cancelBtn}${changing || cancelling ? ` ${styles.cancelBtnOff}` : ''}`}
                              onClick={() => !changing && !cancelling && handleCancel(o)}
                            >
                              <FaRegTrashAlt />
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* ── Decor images modal ── */}
                    <Modal
                      isOpen={!!decorModal[o.id]}
                      onRequestClose={() => setDecorModal(prev => ({ ...prev, [o.id]: false }))}
                      className="custom-modal"
                      overlayClassName="custom-modal-overlay center"
                      ariaHideApp={true}
                    >
                      <MdClose
                        onClick={() => setDecorModal(prev => ({ ...prev, [o.id]: false }))}
                        className="close over cursor"
                      />
                      <Swiper
                        modules={[Autoplay]}
                        autoplay={{ delay: 5000, disableOnInteraction: false }}
                        loop={o.decorImgs?.length > 1}
                      >
                        {o.decorImgs?.map((img, idx) => (
                          <SwiperSlide key={idx}>
                            <ProductImage url={img.image_url} alt={`ديكور ${idx + 1}`} loading="lazy" className="mainImage" />
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </Modal>

                    {/* ── Example image modal ── */}
                    <Modal
                      isOpen={!!exampleModal[o.id]}
                      onRequestClose={() => setExampleModal(prev => ({ ...prev, [o.id]: false }))}
                      className="custom-modal"
                      overlayClassName="custom-modal-overlay center"
                      ariaHideApp={true}
                    >
                      <MdClose
                        onClick={() => setExampleModal(prev => ({ ...prev, [o.id]: false }))}
                        className="close over cursor"
                      />
                      <ProductImage url={o.exampleImg?.image_url} alt="مثال" loading="lazy" className="mainImage" />
                    </Modal>
                  </div>
                );
              })}

              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  disabled={loading}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SpecialOrdersPanel;
